import ProfileDropdown from '@/components/ProfileDropdown';
import ProjectSwitcher from '@/components/ProjectSwitcher';
import WhatsAppWarningModal from '@/components/WhatsAppWarningModal';
import { projectsService } from '@/services/projectsService';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useParams } from 'react-router-dom';

/**
 * Project layout component with header, sidebar, and content area for project-specific pages
 * @returns {JSX.Element} ProjectLayout component
 */
const ProjectLayout = () => {
  const location = useLocation();
  const { projectId } = useParams();
  const [showWhatsAppWarning, setShowWhatsAppWarning] = useState(false);

  // Fetch project details
  const { data: projectData, isLoading: projectLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsService.getProject(projectId),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Extract project data - API returns { success: true, data: { ... } }
  const project = projectData?.data || {};

  // Show warning modal if WhatsApp is not connected when project data loads
  useEffect(() => {
    if (!projectData || !projectData.data) return;

    const actualProject = projectData.data;
    const isWhatsappConnected = actualProject.isWhatsappConnected;

    // Only show on initial load or when navigating to project routes
    // Don't show if already on whatsapp-configurations page
    if (location.pathname.includes('whatsapp-configurations')) {
      setShowWhatsAppWarning(false);
      return;
    }

    // Show modal if WhatsApp is not connected (false, undefined, or null)
    if (
      isWhatsappConnected === false ||
      isWhatsappConnected === undefined ||
      isWhatsappConnected === null
    ) {
      setShowWhatsAppWarning(true);
    } else if (isWhatsappConnected === true) {
      setShowWhatsAppWarning(false);
    }
  }, [projectData, location.pathname]);

  const isActive = path => {
    return location.pathname === path;
  };

  const navigation = [
    {
      name: 'Dashboard',
      href: `/projects/${projectId}/dashboard`,
      icon: (
        <svg
          className="h-5 w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z"
          />
        </svg>
      ),
    },
    {
      name: 'Contacts',
      href: `/projects/${projectId}/contacts`,
      icon: (
        <svg
          className="h-5 w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
    },
    {
      name: 'Broadcasts',
      href: `/projects/${projectId}/broadcasts`,
      icon: (
        <svg
          className="h-5 w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
          />
        </svg>
      ),
    },
    {
      name: 'Conversations',
      href: `/projects/${projectId}/conversations`,
      icon: (
        <svg
          className="h-5 w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
      ),
    },
    {
      name: 'Templates',
      href: `/projects/${projectId}/templates`,
      icon: (
        <svg
          className="h-5 w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
    },
    {
      name: 'Automations',
      href: `/projects/${projectId}/flows`,
      icon: (
        <svg
          className="h-5 w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
      ),
    },
    {
      name: 'WhatsApp Setup',
      href: `/projects/${projectId}/whatsapp-configurations`,
      icon: (
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
        </svg>
      ),
    },
    // {
    //   name: 'Developer Hub',
    //   href: `/projects/${projectId}/developer-hub`,
    //   icon: (
    //     <svg
    //       className="h-5 w-5"
    //       fill="none"
    //       stroke="currentColor"
    //       viewBox="0 0 24 24"
    //     >
    //       <path
    //         strokeLinecap="round"
    //         strokeLinejoin="round"
    //         strokeWidth={2}
    //         d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
    //       />
    //     </svg>
    //   ),
    // },
  ];

  const manageNavigation = [
    {
      name: 'Tags',
      href: `/projects/${projectId}/tags`,
      icon: (
        <svg
          className="h-5 w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
          />
        </svg>
      ),
    },
  ];

  const [isManageOpen, setIsManageOpen] = useState(
    location.pathname.includes('/tags')
  );

  return (
    <div className="h-screen w-screen flex overflow-hidden m-0 p-0">
      {/* Sidebar */}
      <aside className="w-64 bg-white shadow-sm border-r border-gray-200 flex-shrink-0 flex flex-col h-full">
        {/* Logo Section */}
        <div className="flex items-center px-6 border-b border-gray-200">
          <Link to="/projects" className="flex-shrink-0">
            <img
              src="/Integrable_Logo.svg"
              alt="Integrable Logo"
              className="h-16 w-auto"
            />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 mt-5 px-2 overflow-y-auto">
          {navigation.map(item => {
            // Re-reading: "until whatsapp is connected, in sidebar of project, keep options disabled, other than whatsapp setup"
            // Update: keep dashboard and developer hub enabled as well.
            const isActuallyDisabled =
              !project.isWhatsappConnected &&
              item.name !== 'Dashboard' &&
              item.name !== 'Contacts' &&
              item.name !== 'WhatsApp Setup' &&
              item.name !== 'Developer Hub';

            return isActuallyDisabled ? (
              <div
                key={item.name}
                className="group flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-400 cursor-not-allowed opacity-60"
                title="Please complete whatsapp setup to access this feature"
              >
                <span className="mr-3 text-gray-400">{item.icon}</span>
                {item.name}
              </div>
            ) : (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(item.href)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <span
                  className={`mr-3 ${isActive(item.href) ? 'text-primary-700' : 'text-primary-600'}`}
                >
                  {item.icon}
                </span>
                {item.name}
              </Link>
            );
          })}

          {/* Manage Section */}
          <div className="mt-8">
            <button
              onClick={() => setIsManageOpen(!isManageOpen)}
              className="w-full flex items-center justify-between px-2 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-900 transition-colors focus:outline-none"
            >
              <span>Manage</span>
              <svg
                className={`h-4 w-4 transform transition-transform ${isManageOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {isManageOpen && (
              <div className="mt-1 space-y-1">
                {manageNavigation.map(item => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive(item.href)
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <span
                      className={`mr-3 ${isActive(item.href) ? 'text-primary-700' : 'text-primary-600'}`}
                    >
                      {item.icon}
                    </span>
                    {item.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>
      </aside>

      {/* Right Content Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
          <div className="h-16 flex justify-between items-center px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <div className="flex items-center gap-4">
                <h1 className="text-xl font-semibold text-gray-900">
                  {projectLoading
                    ? 'Loading...'
                    : project.name || 'Project Dashboard'}
                </h1>
                {project.isActive !== undefined && (
                  <>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        project.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {project.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <ProjectSwitcher />
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {/* WhatsApp Connection Status */}
              {project.isWhatsappConnected !== undefined && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">
                    WhatsApp Business API Status:
                  </span>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      project.isWhatsappConnected
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {project.isWhatsappConnected
                      ? 'Connected'
                      : 'Not Connected'}
                  </span>
                </div>
              )}
              {/* Add notifications, etc. here */}
              <ProfileDropdown />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <Outlet />
        </main>
      </div>

      {/* WhatsApp Warning Modal */}
      <WhatsAppWarningModal
        isOpen={showWhatsAppWarning}
        onClose={() => setShowWhatsAppWarning(false)}
        projectId={projectId}
      />
    </div>
  );
};

export default ProjectLayout;
